import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { useState } from "react"
import { useSelector } from "react-redux"

import withGiftWraps from "areas/cart/hoc/with-gift-wraps"
import Checkbox from "components/ui/checkbox"
import Image from "components/ui/image"
import { to_currency } from "helpers/number"
import { useNavigation } from "helpers/use-navigation"
import { cn } from "styles/utils"

import { CardItem } from "../components"

const GiftWrapItem = ({
  giftWrap,
  handleClick,
  selected,
  styleCode,
  tabIndex,
}) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  return (
    <CardItem
      aria-checked={selected}
      aria-label={`${giftWrap.package_name} gift wrap`}
      className="tablet:basis-(--tablet-basis)"
      key={`item-wrap-${giftWrap.package.addon_code}`}
      onClick={handleClick}
      role="radio"
      selected={selected}
      tabIndex={tabIndex}
      {...keyboardProps}
    >
      <Image
        alt={giftWrap.package_name}
        className="h-(--base-width) tablet:h-(--tablet-width) w-(--base-width) tablet:w-(--tablet-width)"
        data-cy={`cart-line-item-gift-wrap-${giftWrap.package.addon_code}-${styleCode}`}
        height="280"
        src={giftWrap.image_path}
        width="280"
      />
      <p className="text-12 tracking-tigher leading-14">
        {giftWrap.package_name}
      </p>
    </CardItem>
  )
}

GiftWrapItem.propTypes = {
  giftWrap: PropTypes.object,
  handleClick: PropTypes.func,
  selected: PropTypes.bool,
  styleCode: PropTypes.string,
  tabIndex: PropTypes.number,
}

const GiftWraps = ({
  giftWrapPrice,
  handleGiftWrap,
  item,
  pkgOptions,
  selectedWrap,
  selectedGiftWrap,
  setValue,
}) => {
  const [toggleGiftWraps, setToggleGiftWraps] = useState(!!selectedWrap)
  const cartWarrantyExp = useSelector(
    (state) => state.experiment.cartWarranty.variant_id
  )

  return cartWarrantyExp === 1 ? (
    <div className="border-grey-200 mt-8 border bg-white p-16">
      <h3 className="text-primary text-16 mb-4 font-bold">Add Gift Wrap</h3>
      <p className="text-13 mb-16">Select a gift wrap of your choice.</p>
      <div
        aria-label="Select a greeting card"
        className="desktop:grid flex grid-cols-2 flex-col gap-16"
        role="radiogroup"
      >
        {(item.gift_package_options || []).slice(1).map((pkg) => {
          const selected =
            selectedGiftWrap?.addon_code === pkg.package.addon_code

          return (
            <div key={`gift-wrap-${item.id}-${pkg.package.addon_code}`}>
              <label
                className="flex cursor-pointer items-center gap-16"
                htmlFor={`gift-wrap-${item.id}-${pkg.package.addon_code}`}
              >
                <input
                  checked={selected}
                  className="sr-only"
                  data-cy={`cart-line-item-gift-wrap-checkbox-${item.style_code}`}
                  id={`gift-wrap-${item.id}-${pkg.package.addon_code}`}
                  onChange={() => setValue("wrap", pkg.package)}
                  type="radio"
                />
                <div
                  aria-hidden
                  className={cn(
                    "h-(--base-width) w-(--base-width) before:z-2 after:h-(--after-width) after:w-(--after-width) after:z-1 before:h-(--before-height) before:w-(--before-width) relative before:absolute before:left-6 before:top-3 before:rotate-45 before:border-b-2 before:border-r-2 before:border-b-white before:border-r-white after:absolute after:rounded-full after:border after:border-black",
                    selected
                      ? "after:bg-primary after:border-primary before:block"
                      : "before:hidden after:bg-white"
                  )}
                  style={{
                    "--after-width": "17px",
                    "--base-width": "18px",
                    "--before-height": "9px",
                    "--before-width": "5px",
                  }}
                />
                <Image
                  alt={`Preview of ${pkg.package_name}`}
                  className="w-56"
                  data-cy={`cart-line-item-gift-wrap-${pkg.package.addon_code}-${item.style_code}`}
                  height="280"
                  src={pkg.image_path}
                  width="280"
                />
                <div>
                  <p className="body-sm mb-8 font-semibold">
                    {pkg.package_name}
                  </p>
                  <p className="body-sm font-semibold">
                    + {to_currency(pkg.package.price, { precision: 2 })}
                  </p>
                </div>
              </label>
            </div>
          )
        })}
      </div>
    </div>
  ) : (
    <>
      <Checkbox
        checked={toggleGiftWraps}
        className="gap-10"
        data-cy={`cart-line-item-gift-wrap-checkbox-${item.style_code}`}
        onChange={() => {
          if (toggleGiftWraps) {
            handleGiftWrap(pkgOptions[0])
            setToggleGiftWraps(false)
          } else {
            handleGiftWrap(pkgOptions[1])
            setToggleGiftWraps(true)
          }
        }}
      >
        Add Gift Wrap (
        {giftWrapPrice > 0
          ? `+${to_currency(giftWrapPrice, { precision: 2 })}`
          : "FREE"}
        )
      </Checkbox>
      <div
        aria-labelledby={`cart-line-item-gift-wrap-group-${item.id}`}
        className="ml-26"
        role="radiogroup"
      >
        <h5
          className="text-12 tracking-tigher leading-12 mb-16 mt-12 font-normal"
          id={`cart-line-item-gift-wrap-group-${item.id}`}
        >
          Select a gift wrap of your choice.
        </h5>
        <FocusScope restoreFocus>
          <div
            className="mb-16 flex justify-start overflow-hidden pb-10"
            data-cy={`cart-line-item-gift-wrap-${item.style_code}`}
            style={{
              "--base-width": "100px",
              "--tablet-basis": "135px",
              "--tablet-width": "111px",
            }}
          >
            {pkgOptions.slice(1).map((p, i) => {
              const selected = selectedWrap
                ? selectedWrap.addon_code === p.package.addon_code
                : p.id === "NONE"

              return (
                <GiftWrapItem
                  giftWrap={p}
                  handleClick={() => {
                    setToggleGiftWraps(true)
                    handleGiftWrap(p)
                  }}
                  key={`item-wrap-${p.package.addon_code}`}
                  selected={selected}
                  styleCode={item.style_code}
                  tabIndex={selected || (!selectedWrap && i === 0) ? 0 : -1}
                />
              )
            })}
          </div>
        </FocusScope>
      </div>
    </>
  )
}

GiftWraps.propTypes = {
  giftWrapPrice: PropTypes.number,
  handleGiftWrap: PropTypes.func,
  item: PropTypes.object,
  pkgOptions: PropTypes.array,
  selectedGiftWrap: PropTypes.object,
  selectedWrap: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  setValue: PropTypes.func,
}

export default withGiftWraps(GiftWraps)
